import React, { useState, useEffect } from 'react';
import './App.css';
import universityData from './data/kdata-undergraduate.json';

const NewPage = ({ onBackToMain }) => {
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedSubTable, setSelectedSubTable] = useState(null);
  const [showSubTables, setShowSubTables] = useState(false);
  const [showDetailTable, setShowDetailTable] = useState(false);
  const [tableData, setTableData] = useState(null);
  const [currentProfile, setCurrentProfile] = useState(1);
  const [isRolledUp, setIsRolledUp] = useState(false);
  
  // Search functionality states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  // Graduate school filter states
  const [graduateFilters, setGraduateFilters] = useState({
    all: true,
    master: false,
    doctoral: false,
    dm: false,
    other: false
  });
  
  // Transfer student filter states
  const [transferFilters, setTransferFilters] = useState({
    all: true,
    secondYear: false,
    thirdYear: false
  });

  // Define profiles data
  const profiles = {
    1: { name: '本科生', color: '#007bff' },
    2: { name: '插班生', color: '#28a745' },
    3: { name: '大学院', color: '#dc3545' }
  };

  // Define handleProfileSwitch function
  const handleProfileSwitch = (profileId) => {
    setCurrentProfile(profileId);
    // Reset selections when switching profiles
    setSelectedTable(null);
    setSelectedSubTable(null);
    setShowSubTables(false);
    setShowDetailTable(false);
    setIsRolledUp(false);
    // Reset graduate filters
    setGraduateFilters({
      all: true,
      master: false,
      doctoral: false,
      dm: false,
      other: false
    });
    // Reset transfer filters
    setTransferFilters({
      all: true,
      secondYear: false,
      thirdYear: false
    });
  };

  // Handle graduate school filter changes
  const handleFilterChange = (filterType, checked) => {
    if (filterType === 'all') {
      if (checked) {
        // If "All" is checked, uncheck all others
        setGraduateFilters({
          all: true,
          master: false,
          doctoral: false,
          dm: false,
          other: false
        });
      }
    } else {
      // If any specific filter is checked, uncheck "All"
      setGraduateFilters(prev => ({
        ...prev,
        all: false,
        [filterType]: checked
      }));
    }
  };
  
  // Handle transfer student filter changes
  const handleTransferFilterChange = (filterType, checked) => {
    if (filterType === 'all') {
      if (checked) {
        // If "All" is checked, uncheck all others
        setTransferFilters({
          all: true,
          secondYear: false,
          thirdYear: false
        });
      }
    } else {
      // If any specific filter is checked, uncheck "All"
      setTransferFilters(prev => ({
        ...prev,
        all: false,
        [filterType]: checked
      }));
    }
  };

  // Load data from JSON file
  useEffect(() => {
    const loadTableData = () => {
      const universities = Object.values(universityData.universities);
      
      const mainTables = universities.map((university, index) => {
        // Get unique departments for this university based on current profile
        let uniqueDepartments;
        let subTables;
        
        if (currentProfile === 3) { // 大学院 profile
          uniqueDepartments = [...new Set(
            university.departments.map(dept => dept.d_department)
          )].filter(dept => dept);
          
          subTables = uniqueDepartments.map((deptName, deptIndex) => {
            const facultiesInDept = university.departments.filter(dept => dept.d_department === deptName);
            
            return {
              id: deptIndex + 1,
              name: deptName,
              color: getRandomColor(deptIndex),
              faculties: facultiesInDept
            };
          });
        } else if (currentProfile === 2) { // 插班生 profile
          uniqueDepartments = [...new Set(
            university.departments.map(dept => dept.t_department)
          )].filter(dept => dept); // Filter out null/empty departments
          
          subTables = uniqueDepartments.map((deptName, deptIndex) => ({
            id: deptIndex + 1,
            name: deptName,
            color: getRandomColor(deptIndex),
            faculties: university.departments.filter(dept => dept.t_department === deptName)
          }));
        } else {
          // Default profile (本科生) - use regular department
          uniqueDepartments = [...new Set(
            university.departments.map(dept => dept.department)
          )].filter(dept => dept); // Filter out null/empty departments
          
          subTables = uniqueDepartments.map((deptName, deptIndex) => ({
            id: deptIndex + 1,
            name: deptName,
            color: getRandomColor(deptIndex),
            faculties: university.departments.filter(dept => dept.department === deptName)
          }));
        }
        
        return {
          id: index + 1,
          name: university.name,
          universityId: university.id,
          color: getRandomColor(index),
          info: university.info,
          subTables: subTables
        };
      });
      
      // Filter out universities with no departments
      const filteredMainTables = mainTables.filter(uni => uni.subTables.length > 0);

      // Create table columns structure based on current profile
      const tableColumns = {};
      filteredMainTables.forEach(mainTable => {
        mainTable.subTables.forEach(subTable => {
          const key = `${mainTable.id}-${subTable.id}`;
          
          // Define columns and data based on profile
          let columns, data;
          
          if (currentProfile === 2) { // 插班生 profile
            columns = ['学院', '学部/学系', '专业/课程', '费用', '其他信息', '二年级', '三年级'];
            data = subTable.faculties.map(faculty => [
              faculty.t_department || '',
              faculty.t_faculty || '',
              faculty.t_course || '',
              faculty.t_fee || '',
              faculty.t_other || '',
              faculty.t_2ndyear || '',
              faculty.t_3rdyear || ''
            ]);
          } else if (currentProfile === 3) { // 大学院 profile
            columns = ['大学院', '学部/学系', '专业/课程', '费用', '其他信息'];
            data = subTable.faculties.map(faculty => [
              faculty.d_graduate_school || '',
              faculty.d_department || '',
              faculty.d_faculty || '',
              faculty.d_course || faculty.d_faculty,
              faculty.d_fee || '',
              faculty.d_other || ''
            ]);
          } else { // Default profile (本科生)
            columns = ['学院', '学部/学系', '专业/课程', '费用', '其他信息'];
            data = subTable.faculties.map(faculty => [
              faculty.department || '',
              faculty.faculty || faculty.department, 
              faculty.course || faculty.faculty,
              faculty.fee || '标准费用',
              faculty.other || '无额外信息'
            ]);
          }
          
          tableColumns[key] = {
            columns,
            data
          };
        });
      });

      setTableData({
        mainTables: filteredMainTables,
        tableColumns
      });
    };

    loadTableData();
  }, [currentProfile]);

  // Helper function to generate colors
  const getRandomColor = (index) => {
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD',
      '#E74C3C', '#3498DB', '#2ECC71', '#E67E22', '#9B59B6', '#1ABC9C',
      '#34495E', '#F39C12', '#8E44AD', '#27AE60', '#D35400', '#2980B9',
      '#C0392B', '#16A085'
    ];
    return colors[index % colors.length];
  };

  const handleTableClick = (table) => {
    setSelectedTable(table);
    setIsRolledUp(true);
    
    // Delay showing sub-tables to allow roll-up animation
    setTimeout(() => {
      setShowSubTables(true);
      setShowDetailTable(false);
      setSelectedSubTable(null);
    }, 500); // Match the animation duration
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubTableClick = (subTable) => {
    setSelectedSubTable(subTable);
    setShowDetailTable(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToGrid = () => {
    setSelectedTable(null);
    setShowSubTables(false);
    setShowDetailTable(false);
    setSelectedSubTable(null);
    setIsRolledUp(false);
  };

  const handleBackToSubTables = () => {
    setShowDetailTable(false);
    setSelectedSubTable(null);
  };

  // Search function
  const handleSearch = (query) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const results = [];
    const searchTerm = query.toLowerCase();

    // Search through all universities and their data
    Object.values(universityData.universities).forEach((university) => {
      // Search university name
      if (university.name.toLowerCase().includes(searchTerm)) {
        results.push({
          type: 'university',
          university: university.name,
          match: university.name,
          data: university
        });
      }

      // Search university info
      if (university.info && university.info.toLowerCase().includes(searchTerm)) {
        results.push({
          type: 'info',
          university: university.name,
          match: university.info,
          data: university
        });
      }

      // Search through departments
      university.departments.forEach((dept) => {
        // Search department name
        if (dept.department && dept.department.toLowerCase().includes(searchTerm)) {
          results.push({
            type: 'department',
            university: university.name,
            department: dept.department,
            match: dept.department,
            data: dept
          });
        }

        // Search faculty name
        if (dept.faculty && dept.faculty.toLowerCase().includes(searchTerm)) {
          results.push({
            type: 'faculty',
            university: university.name,
            department: dept.department,
            faculty: dept.faculty,
            match: dept.faculty,
            data: dept
          });
        }

        // Search course
        if (dept.course && dept.course.toLowerCase().includes(searchTerm)) {
          results.push({
            type: 'course',
            university: university.name,
            department: dept.department,
            faculty: dept.faculty,
            course: dept.course,
            match: dept.course,
            data: dept
          });
        }

        // Search fee information
        if (dept.fee && dept.fee.toLowerCase().includes(searchTerm)) {
          results.push({
            type: 'fee',
            university: university.name,
            department: dept.department,
            faculty: dept.faculty,
            match: dept.fee,
            data: dept
          });
        }

        // Search other information
        if (dept.other && dept.other.toLowerCase().includes(searchTerm)) {
          results.push({
            type: 'other',
            university: university.name,
            department: dept.department,
            faculty: dept.faculty,
            match: dept.other,
            data: dept
          });
        }
      });
    });

    // Remove duplicates and limit results
    const uniqueResults = results.filter((result, index, self) => 
      index === self.findIndex(r => 
        r.type === result.type && 
        r.university === result.university && 
        r.match === result.match
      )
    ).slice(0, 20); // Limit to 20 results

    setSearchResults(uniqueResults);
    setShowSearchResults(true);
  };

  // Handle search result click
  const handleSearchResultClick = (result) => {
    // Find the university in tableData
    const university = tableData.mainTables.find(uni => uni.name === result.university);
    
    if (university) {
      // Navigate to the university
      handleTableClick(university);
      
      // If it's a department/faculty result, also navigate to the department
      if (result.department && (result.type === 'department' || result.type === 'faculty' || result.type === 'course')) {
        setTimeout(() => {
          const department = university.subTables.find(dept => dept.name === result.department);
          if (department) {
            handleSubTableClick(department);
          }
        }, 600); // Wait for university selection animation
      }
    }
    
    // Clear search
    setSearchQuery('');
    setShowSearchResults(false);
  };

  // Close search results when clicking outside
  const handleSearchBlur = () => {
    setTimeout(() => {
      setShowSearchResults(false);
    }, 200); // Delay to allow result clicks
  };

  // Helper function to get result type labels
  const getResultTypeLabel = (type) => {
    const labels = {
      university: '大学',
      department: '学院',
      faculty: '专业',
      course: '课程',
      fee: '费用',
      info: '信息',
      other: '其他'
    };
    return labels[type] || type;
  };

  // Show loading if data is not yet loaded
  if (!tableData) {
    return (
      <div className="new-page">
        <div className="loading-container">
          <h2>加载大学数据中...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="new-page">
      {/* Navigation and Profile Switcher */}
      <div className="navigation-header">
        <div className="left-section">
          <button 
            className="back-button"
            onClick={onBackToMain}
          >
            中文授课
          </button>
          <img 
            src="/assets/icon.png" 
            alt="Icon" 
            className="header-icon"
          />
        </div>
        
        {/* Search Component */}
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="搜索大学、学院、专业..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            onBlur={handleSearchBlur}
            onFocus={() => searchQuery && setShowSearchResults(true)}
          />
          <div className="search-icon">🔍</div>
          
          {/* Search Results Dropdown */}
          {showSearchResults && searchResults.length > 0 && (
            <div className="search-results">
              {searchResults.map((result, index) => (
                <div
                  key={index}
                  className="search-result-item"
                  onClick={() => handleSearchResultClick(result)}
                >
                  <div className="result-type">{getResultTypeLabel(result.type)}</div>
                  <div className="result-university">{result.university}</div>
                  {result.department && (
                    <div className="result-department">{result.department}</div>
                  )}
                  <div className="result-match">{result.match}</div>
                </div>
              ))}
            </div>
          )}
          
          {/* No Results Message */}
          {showSearchResults && searchResults.length === 0 && searchQuery && (
            <div className="search-results">
              <div className="no-results">未找到相关结果</div>
            </div>
          )}
        </div>
        
        <div className="profile-switcher">
          {Object.entries(profiles).map(([id, profile]) => (
            <button
              key={id}
              className={`profile-button ${currentProfile === parseInt(id) ? 'active' : ''}`}
              style={{
                backgroundColor: currentProfile === parseInt(id) ? profile.color : 'transparent',
                borderColor: profile.color,
                color: currentProfile === parseInt(id) ? 'white' : profile.color
              }}
              onClick={() => handleProfileSwitch(parseInt(id))}
            >
              {profile.name}
            </button>
          ))}
        </div>
      </div>

      {/* Main Table Selection Grid */}
      <div className="main-tables-container">
        {!isRolledUp && <h2>选择大学</h2>}
        
        {/* Selected University Button (when rolled up) */}
        {isRolledUp && selectedTable && (
          <div className="selected-university-container">
            <button className="back-button" onClick={handleBackToGrid}>
              ← 返回大学选择
            </button>
            <div className="selected-university-button">
              <div className="table-button-content">
                <h3>{selectedTable.name}</h3>
                <p>{selectedTable.subTables.length} 个学院</p>
              </div>
            </div>
          </div>
        )}
        
        {/* University Grid */}
        <div className={`tables-grid ${isRolledUp ? 'rolled-up' : ''}`}>
          {tableData.mainTables.map((table) => (
            <button
              key={table.id}
              className={`table-button ${
                isRolledUp && selectedTable && table.id !== selectedTable.id ? 'hidden' : ''
              } ${
                isRolledUp && selectedTable && table.id === selectedTable.id ? 'selected-rolled-up' : ''
              }`}
              style={{ backgroundColor: table.color }}
              onClick={() => !isRolledUp && handleTableClick(table)}
            >
              <div className="table-button-content">
                <h3>{table.name}</h3>
                <p>{table.subTables.length} 个学院</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Sub-tables (Departments) */}
      {showSubTables && !showDetailTable && selectedTable && (
        <div className="sub-tables-container slideDown">
          <div className="sub-tables-header">
            <h2>选择学院</h2>
          </div>
          
          <div className="sub-tables-grid">
            {selectedTable.subTables.map((subTable) => (
              <button
                key={subTable.id}
                className="sub-table-button"
                style={{ backgroundColor: subTable.color }}
                onClick={() => handleSubTableClick(subTable)}
              >
                <div className="sub-table-content">
                  <h4>{subTable.name}</h4>
                  <p>{subTable.faculties.length} 个专业</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Detail Table */}
      {showDetailTable && selectedTable && selectedSubTable && (
        <div className="detail-table-container slideDown">
          <div className="detail-table-header">
            <button className="back-button" onClick={handleBackToSubTables}>
              ← 返回学院选择
            </button>
            <h2>{selectedTable.name} - {selectedSubTable.name}</h2>
          </div>
          
          {/* Graduate School Filters */}
          {currentProfile === 3 && (
            <div className="graduate-filters">
              <div className="filter-group">
                <label className="filter-checkbox">
                  <input
                    type="checkbox"
                    checked={graduateFilters.all}
                    onChange={(e) => handleFilterChange('all', e.target.checked)}
                  />
                  <span>全部</span>
                </label>
                <label className="filter-checkbox">
                  <input
                    type="checkbox"
                    checked={graduateFilters.master}
                    onChange={(e) => handleFilterChange('master', e.target.checked)}
                  />
                  <span>硕士</span>
                </label>
                <label className="filter-checkbox">
                  <input
                    type="checkbox"
                    checked={graduateFilters.doctoral}
                    onChange={(e) => handleFilterChange('doctoral', e.target.checked)}
                  />
                  <span>博士</span>
                </label>
                <label className="filter-checkbox">
                  <input
                    type="checkbox"
                    checked={graduateFilters.dm}
                    onChange={(e) => handleFilterChange('dm', e.target.checked)}
                  />
                  <span>DM</span>
                </label>
                <label className="filter-checkbox">
                  <input
                    type="checkbox"
                    checked={graduateFilters.other}
                    onChange={(e) => handleFilterChange('other', e.target.checked)}
                  />
                  <span>其他</span>
                </label>
              </div>
            </div>
          )}
          
          {/* Transfer Student Filters */}
          {currentProfile === 2 && (
            <div className="transfer-filters">
              <div className="filter-group">
                <label className="filter-checkbox">
                  <input
                    type="checkbox"
                    checked={transferFilters.all}
                    onChange={(e) => handleTransferFilterChange('all', e.target.checked)}
                  />
                  <span>全部</span>
                </label>
                <label className="filter-checkbox">
                  <input
                    type="checkbox"
                    checked={transferFilters.secondYear}
                    onChange={(e) => handleTransferFilterChange('secondYear', e.target.checked)}
                  />
                  <span>二年级</span>
                </label>
                <label className="filter-checkbox">
                  <input
                    type="checkbox"
                    checked={transferFilters.thirdYear}
                    onChange={(e) => handleTransferFilterChange('thirdYear', e.target.checked)}
                  />
                  <span>三年级</span>
                </label>
              </div>
            </div>
          )}
          
          <div className="table-wrapper">
            {(() => {
              const key = `${selectedTable.id}-${selectedSubTable.id}`;
              const tableInfo = tableData.tableColumns[key];
              
              if (!tableInfo || !tableInfo.data.length) {
                return (
                  <div className="no-data">
                    <p>暂无该学院的详细信息</p>
                  </div>
                );
              }
              
              // Filter data for graduate school profile
              let filteredData = tableInfo.data;
              if (currentProfile === 3 && !graduateFilters.all) {
                filteredData = tableInfo.data.filter((row, rowIndex) => {
                  // Get the original faculty data to check T/F values
                  const faculty = selectedSubTable.faculties[rowIndex];
                  
                  if (!faculty) return false;
                  
                  // Treat null values as 'F'
                  const masterValue = faculty.d_master === null ? 'F' : (faculty.d_master || 'F');
                  const doctoralValue = faculty.d_doctoral === null ? 'F' : (faculty.d_doctoral || 'F');
                  const dmValue = faculty.d_dm === null ? 'F' : (faculty.d_dm || 'F');
                  
                  let shouldShow = false;
                  
                  if (graduateFilters.master && masterValue === 'T') {
                    shouldShow = true;
                  }
                  if (graduateFilters.doctoral && doctoralValue === 'T') {
                    shouldShow = true;
                  }
                  if (graduateFilters.dm && dmValue === 'T') {
                    shouldShow = true;
                  }
                  if (graduateFilters.other && masterValue === 'F' && doctoralValue === 'F' && dmValue === 'F') {
                    shouldShow = true;
                  }
                  
                  return shouldShow;
                });
              }
              
              // Filter data for transfer student profile
              if (currentProfile === 2 && !transferFilters.all) {
                filteredData = filteredData.filter((row, rowIndex) => {
                  // Get the original faculty data to check T/F values
                  const faculty = selectedSubTable.faculties[rowIndex];
                  
                  if (!faculty) return false;
                  
                  // Treat null values as 'F'
                  const secondYearValue = faculty.t_2ndyear === null ? 'F' : (faculty.t_2ndyear || 'F');
                  const thirdYearValue = faculty.t_3rdyear === null ? 'F' : (faculty.t_3rdyear || 'F');
                  
                  let shouldShow = false;
                  
                  if (transferFilters.secondYear && secondYearValue === 'T') {
                    shouldShow = true;
                  }
                  if (transferFilters.thirdYear && thirdYearValue === 'T') {
                    shouldShow = true;
                  }
                  
                  return shouldShow;
                });
              }
              
              return (
                <table className="detail-table">
                  <thead>
                    <tr>
                      {tableInfo.columns.map((column, index) => {
                        // Hide the 硕士, 博士, DM columns in profile 3
                        if (currentProfile === 3 && (column === '硕士' || column === '博士' || column === 'DM')) {
                          return null;
                        }
                        // Hide the 二年级, 三年级 columns in profile 2
                        if (currentProfile === 2 && (column === '二年级' || column === '三年级')) {
                          return null;
                        }
                        return <th key={index}>{column}</th>;
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => {
                          // Hide the T/F values for 硕士, 博士, DM columns in profile 3
                          const columnName = tableInfo.columns[cellIndex];
                          if (currentProfile === 3 && (columnName === '硕士' || columnName === '博士' || columnName === 'DM')) {
                            return null;
                          }
                          // Hide the T/F values for 二年级, 三年级 columns in profile 2
                          if (currentProfile === 2 && (columnName === '二年级' || columnName === '三年级')) {
                            return null;
                          }
                          return <td key={cellIndex}>{cell}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })()} 
          </div>
          
          {/* University Info Section */}
          {selectedTable && selectedTable.info && (
            <div className="university-info-section">
              <h3>大学信息</h3>
              <div className="university-info-content">
                {selectedTable.info}
              </div>
            </div>
          )}
        </div>
      )}

      <style jsx>{`
        .loading-container {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 50vh;
          color: #666;
        }
        
        .navigation-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          background: white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          margin-bottom: 20px;
        }
        
        .left-section {
          display: flex;
          align-items: center;
          gap: 15px;
        }
        
        .header-icon {
          width: 60px;
          height: 60px;
          object-fit: contain;
        }
        
        .profile-switcher {
          display: flex;
          gap: 10px;
        }
        
        .profile-button {
          padding: 8px 16px;
          border: 2px solid;
          border-radius: 20px;
          background: transparent;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.3s ease;
        }
        
        .profile-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.2);
        }
        
        .search-container {
          position: relative;
          flex: 1;
          max-width: 400px;
          margin: 0 20px;
          display: flex;
          justify-content: center;
        }
        
        .search-input {
          width: 100%;
          padding: 10px 40px 10px 15px;
          border: 2px solid #ddd;
          border-radius: 25px;
          font-size: 14px;
          outline: none;
          transition: border-color 0.3s ease;
        }
        
        .search-input:focus {
          border-color: #007bff;
        }
        
        .search-icon {
          position: absolute;
          right: 15px;
          top: 50%;
          transform: translateY(-50%);
          color: #666;
          pointer-events: none;
        }
        
        .search-results {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          max-height: 400px;
          overflow-y: auto;
          z-index: 1000;
          margin-top: 5px;
        }
        
        .search-result-item {
          padding: 12px 15px;
          border-bottom: 1px solid #eee;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }
        
        .search-result-item:hover {
          background-color: #f8f9fa;
        }
        
        .search-result-item:last-child {
          border-bottom: none;
        }
        
        .result-type {
          font-size: 12px;
          color: #007bff;
          font-weight: 500;
          margin-bottom: 2px;
        }
        
        .result-university {
          font-weight: 600;
          color: #333;
          margin-bottom: 2px;
        }
        
        .result-department {
          font-size: 13px;
          color: #666;
          margin-bottom: 2px;
        }
        
        .result-match {
          font-size: 13px;
          color: #333;
        }
        
        .no-results {
          padding: 15px;
          text-align: center;
          color: #666;
          font-style: italic;
        }
        
        .main-tables-container {
          padding: 20px;
        }
        
        .selected-university-container {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-bottom: 20px;
          padding: 20px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        
        .selected-university-button {
          flex: 1;
          padding: 20px;
          background: linear-gradient(135deg,rgb(102, 155, 234) 0%,rgb(255, 103, 65) 100%);
          color: white;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        
        .tables-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
          margin-top: 20px;
          transition: all 0.5s ease;
        }
        
        .tables-grid.rolled-up {
          height: 0;
          overflow: hidden;
          opacity: 0;
          margin: 0;
        }
        
        .table-button {
          padding: 30px 20px;
          border: black;
          border-radius: 12px;
          color: white;
          cursor: pointer;
          transition: all 0.5s ease;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          transform: scale(1);
          opacity: 1;
        }
        
        .table-button.hidden {
          transform: scale(0);
          opacity: 0;
          height: 0;
          padding: 0;
          margin: 0;
        }
        
        .table-button.selected-rolled-up {
          transform: scale(0);
          opacity: 0;
          height: 0;
          padding: 0;
          margin: 0;
        }
        
        .table-button:not(.hidden):not(.selected-rolled-up):hover {
          transform: translateY(-5px) scale(1);
          box-shadow: 0 8px 15px rgba(0, 0, 0, 0.2);
        }
        
        .table-button-content h3 {
          margin: 0 0 10px 0;
          font-size: 1.2em;
        }
        
        .table-button-content p {
          margin: 0;
          opacity: 0.9;
          font-size: 0.9em;
        }
        
        .sub-tables-container {
          padding: 20px;
        }
        
        .sub-tables-header {
          margin-bottom: 20px;
        }
        
        .sub-tables-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
        }
        
        .sub-table-button {
          padding: 20px 15px;
          border: none;
          border-radius: 10px;
          color: white;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 3px 5px rgba(0, 0, 0, 0.1);
        }
        
        .sub-table-button:hover {
          transform: translateY(-3px);
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
        }
        
        .sub-table-content h4 {
          margin: 0 0 8px 0;
          font-size: 1.1em;
        }
        
        .sub-table-content p {
          margin: 0;
          opacity: 0.9;
          font-size: 0.85em;
        }
        
        .detail-table-container {
          padding: 20px;
        }
        
        .detail-table-header {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-bottom: 20px;
        }
        
        .table-wrapper {
          overflow-x: auto;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .detail-table {
          width: 100%;
          border-collapse: collapse;
          min-width: 800px;
        }
        
        .detail-table th,
        .detail-table td {
          padding: 8px 12px;
          text-align: left;
          border-bottom: 1px solid #eee;
          font-size: 0.9em;
        }
        
        .detail-table th {
          background-color: #f8f9fa;
          font-weight: 600;
          color: #333;
          white-space: nowrap;
        }
        
        .detail-table tr:hover {
          background-color: #f8f9fa;
        }
        
        .no-data {
          padding: 40px;
          text-align: center;
          color: #666;
        }
        
        .university-info-section {
          margin-top: 30px;
          padding: 20px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .university-info-section h3 {
          margin: 0 0 15px 0;
          color: #333;
          font-size: 1.2em;
          font-weight: 600;
          border-bottom: 2px solid #007bff;
          padding-bottom: 8px;
        }
        
        .university-info-content {
          color: #555;
          line-height: 1.6;
          font-size: 0.95em;
        }
        
        .back-button {
          padding: 10px 20px;
          background: #6c757d;
          color: white;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          transition: background-color 0.3s ease;
        }
        
        .back-button:hover {
          background: #5a6268;
        }
        
        .graduate-filters {
          margin: 20px 0;
          padding: 15px;
          background: #f8f9fa;
          border-radius: 8px;
          border: 1px solid #dee2e6;
        }
        
        .filter-group {
          display: flex;
          gap: 20px;
          align-items: center;
          flex-wrap: wrap;
        }
        
        .filter-checkbox {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          color: #495057;
          transition: color 0.2s ease;
        }
        
        .filter-checkbox:hover {
          color: #007bff;
        }
        
        .filter-checkbox input[type="checkbox"] {
          width: 16px;
          height: 16px;
          cursor: pointer;
          accent-color: #007bff;
        }
        
        .filter-checkbox span {
          user-select: none;
        }
        
        .slideDown {
          animation: slideDown 0.3s ease-out;
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @media (max-width: 768px) {
          .navigation-header {
            flex-direction: column;
            gap: 15px;
          }
          
          .left-section {
            justify-content: center;
          }
          
          .search-container {
            max-width: 100%;
            margin: 0;
          }
          
          .selected-university-container {
            flex-direction: column;
            text-align: center;
          }
          
          .tables-grid {
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 15px;
          }
          
          .sub-tables-grid {
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default NewPage;